import React, { useEffect, useMemo, useState } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { db } from '../../../../firebase';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import {
  getDeliveryStatusPresentation,
  getRecipientStatusLabel,
  getToneClasses,
} from '../../../../utils/emailDeliveryStatus';

/**
 * Email Log dialog — shows every email sent about a specific patient,
 * including per-recipient delivery status from the SendGrid webhook.
 *
 * Opened from the "View email log" item in PatientInfoPanel's
 * View Actions dropdown. The same PatientInfoPanel is mounted by all
 * three overview pages (ADHD, Autism, CYP ADHD), so this dialog is
 * available everywhere without page-specific wiring.
 *
 * Data source:
 *   patients/{id}/messageHistory — emails logged by the tracked
 *   _send_email_and_log funnel, with delivery status fields updated
 *   by the SendGrid Event Webhook.
 *
 * Layout per email entry (designed to eliminate duplication):
 *   Line 1:  [Delivery badge]  timestamp          — overall status at a glance
 *   Line 2:  Subject line                         — what the email is about
 *   Line 3:  notification_type                    — one subtle category label
 *   Table:   To/CC  email@addr  [Status badge]    — role + address + status
 *            (failure reason underneath failed recipient)
 *
 * Design rules applied: no adjacent duplication (To/CC merged into the
 * recipient table), semantic colour + icon + text on every status, calm
 * layout (one block per email, clear hierarchy).
 */
export default function MessageHistoryDialog({ isOpen, onClose, patientId }) {
  // UI state for remote data lifecycle
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);

  // Derived presentation helpers
  const hasItems = useMemo(() => Array.isArray(items) && items.length > 0, [items]);

  // Fetch email history from messageHistory subcollection when the dialog opens
  useEffect(() => {
    if (!isOpen || !patientId) return;

    let isCurrent = true;
    (async () => {
      setIsLoading(true);
      setError('');
      try {
        // Fetch emails from the messageHistory subcollection, ordered by sent
        // time descending. Capped at 100 to keep the UI responsive.
        const messagesRef = collection(db, 'patients', patientId, 'messageHistory');
        const messagesQ = query(messagesRef, orderBy('sentAt', 'desc'), limit(100));
        const messagesSnap = await getDocs(messagesQ);

        if (!isCurrent) return;

        const timelineItems = messagesSnap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: `msg-${d.id}`,
            at: data.sentAt || null,
            title: data.subject || data.reasonText || 'Notification',
            meta: {
              to: data.to || [],
              cc: data.cc || [],
              type: data.type || '',
              reasonCode: data.reasonCode || '',
              deliveryStatus: data.deliveryStatus || null,
              deliveryFailureReason: data.deliveryFailureReason || null,
              lastDeliveryEventAt: data.lastDeliveryEventAt || null,
              recipients: data.recipients || [],
            }
          };
        });

        // Already sorted by Firestore orderBy; set directly
        setItems(timelineItems);
      } catch (e) {
        console.error('Failed to load email history:', e);
        setError('Failed to load emails. Please try again later.');
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    })();

    return () => {
      isCurrent = false;
    };
  }, [isOpen, patientId]);

  /** Format a Firestore timestamp to a human-readable date/time string. */
  const formatTimestamp = (ts) => {
    try {
      if (!ts) return 'Unknown time';
      const date = ts.toDate ? ts.toDate() : new Date(ts.seconds ? ts.seconds * 1000 : ts);
      return date.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch (_) {
      return 'Unknown time';
    }
  };

  /** Format an ISO timestamp string (from the webhook) for display. */
  const formatIsoTimestamp = (isoStr) => {
    if (!isoStr) return null;
    try {
      const date = new Date(isoStr);
      if (isNaN(date.getTime())) return null;
      return date.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch (_) {
      return null;
    }
  };

  /**
   * Delivery status badge using semantic colours (green/amber/red/gray).
   * Renders colour + icon + text label per design rules (meaning never
   * rides on colour alone).
   */
  const DeliveryStatusBadge = ({ status }) => {
    const presentation = getDeliveryStatusPresentation(status);
    const toneClasses = getToneClasses(presentation.tone);

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border ${toneClasses.bg} ${toneClasses.text} ${toneClasses.border}`}
      >
        <StatusIcon icon={presentation.icon} />
        {presentation.label}
      </span>
    );
  };

  /** Small inline icon for the delivery status badge. */
  const StatusIcon = ({ icon }) => {
    switch (icon) {
      case 'check':
        return (
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'clock':
        return (
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  /**
   * Build a unified recipients list that merges the To/CC role labels
   * with per-recipient delivery status. When the webhook has populated
   * per-recipient status rows, each row includes email + role + status.
   * When no per-recipient data exists (legacy docs), we fall back to
   * the flat to/cc arrays and show no individual status badge.
   */
  const buildRecipientRows = (meta) => {
    const recipientMap = {};
    // Seed from per-recipient data (webhook-enriched)
    if (Array.isArray(meta.recipients) && meta.recipients.length > 0) {
      meta.recipients.forEach((r) => {
        recipientMap[r.email] = {
          email: r.email,
          role: r.role || 'to',
          status: r.status || null,
          reason: r.reason || null,
        };
      });
    }
    // Ensure all To/CC addresses are represented (some may predate the
    // recipient tracking or be missing from the webhook data).
    (meta.to || []).forEach((email) => {
      if (!recipientMap[email]) {
        recipientMap[email] = { email, role: 'to', status: null, reason: null };
      }
    });
    (meta.cc || []).forEach((email) => {
      if (!recipientMap[email]) {
        recipientMap[email] = { email, role: 'cc', status: null, reason: null };
      }
    });
    // Sort: To before CC, then alphabetical within each group
    return Object.values(recipientMap).sort((a, b) => {
      if (a.role !== b.role) return a.role === 'to' ? -1 : 1;
      return a.email.localeCompare(b.email);
    });
  };

  /**
   * Unified recipient table that replaces separate To/CC lines and
   * per-recipient status rows. Each row shows: role label, email address,
   * and (when available) a per-recipient delivery status badge.
   * Failure reasons appear on a second line under the failed recipient.
   */
  const RecipientTable = ({ meta }) => {
    const rows = buildRecipientRows(meta);
    if (rows.length === 0) return null;

    return (
      <div className="mt-2 space-y-1">
        {rows.map((r, idx) => {
          const statusLabel = r.status ? getRecipientStatusLabel(r.status) : null;
          const presentation = r.status ? getDeliveryStatusPresentation(r.status) : null;
          const toneClasses = presentation ? getToneClasses(presentation.tone) : null;

          return (
            <div key={`${r.email}-${idx}`}>
              {/* Recipient row: role + email + status badge */}
              <div className="flex items-center gap-2 text-xs">
                <span className="w-6 text-gray-400 font-semibold uppercase shrink-0">
                  {r.role === 'cc' ? 'CC' : 'To'}
                </span>
                <span className="text-gray-700 font-mono truncate">{r.email}</span>
                {statusLabel && toneClasses && (
                  <span className={`inline-flex items-center px-1.5 py-0 rounded text-xs shrink-0 ${toneClasses.bg} ${toneClasses.text}`}>
                    {statusLabel}
                  </span>
                )}
              </div>
              {/* Failure reason shown underneath the affected recipient only */}
              {r.reason && (
                <div className="ml-8 text-xs text-red-500 italic truncate" title={r.reason}>
                  {r.reason}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  /**
   * Render a compact notification type label. Shows the type once;
   * omits the reason code when it matches the type to avoid duplication.
   */
  const TypeLabel = ({ type, reasonCode }) => {
    if (!type) return null;
    // Humanise underscore-delimited type codes for display
    const displayType = type.replace(/_/g, ' ');
    // Only show reason if it adds information beyond the type
    const showReason = reasonCode && reasonCode !== type;
    return (
      <div className="mt-1 text-xs text-gray-400">
        {displayType}
        {showReason && <span className="ml-1.5 text-gray-400">/ {reasonCode.replace(/_/g, ' ')}</span>}
      </div>
    );
  };

  return (
    <AlertDialog.Root open={isOpen} onOpenChange={(open) => !open && onClose && onClose()}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto z-50">
          <AlertDialog.Title className="text-xl font-semibold mb-1">Email log</AlertDialog.Title>
          <AlertDialog.Description className="text-sm text-gray-500 mb-4">
            Emails sent about this patient, including delivery status where available.
          </AlertDialog.Description>

          {isLoading && (
            <div className="w-full h-20 flex items-center justify-center text-gray-600">
              <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading emails...
            </div>
          )}

          {!isLoading && error && (
            <div className="p-3 mt-2 text-sm font-semibold text-red-600 bg-red-50 rounded-md">
              {error}
            </div>
          )}

          {!isLoading && !error && !hasItems && (
            <div className="p-3 mt-2 text-sm font-semibold text-gray-600 bg-gray-50 rounded-md">
              No emails have been sent about this patient yet.
            </div>
          )}

          {!isLoading && !error && hasItems && (
            <div className="mt-2">
              {/* Email timeline list */}
              <ul className="relative border-l-2 border-gray-200 ml-3">
                {items.map((item) => (
                  <li key={item.id} className="mb-6 ml-4">
                    {/* Dot */}
                    <div className="absolute w-3 h-3 bg-violet-600 rounded-full -left-1.5 border border-white" />

                    {/* Line 1: delivery status badge + timestamp */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <DeliveryStatusBadge status={item.meta?.deliveryStatus} />
                      <span className="text-xs text-gray-500">{formatTimestamp(item.at)}</span>
                    </div>

                    {/* Line 2: subject */}
                    <div className="mt-1 text-sm font-semibold text-gray-800">
                      {item.title}
                    </div>

                    {/* Line 3: notification type (compact, de-emphasised) */}
                    <TypeLabel type={item.meta?.type} reasonCode={item.meta?.reasonCode} />

                    {/* Recipient table: role + email + per-recipient status */}
                    <RecipientTable meta={item.meta} />

                    {/* Subtle last-update timestamp for non-terminal statuses */}
                    {item.meta?.lastDeliveryEventAt && (
                      <div className="mt-1.5 text-xs text-gray-400">
                        Updated {formatIsoTimestamp(item.meta.lastDeliveryEventAt)}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                className="px-4 py-2 text-sm font-semibold rounded-md transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Close
              </button>
            </AlertDialog.Cancel>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
